/**
 * CompanyDetectedSheet — modal bottom sheet shown when tarjeta OCR
 * detects a company name as the vehicle owner.
 *
 * Frictionless: never blocks listing, always lets user skip.
 * Pre-fills org name from detected legal name.
 */
import type { ReactNode } from 'react'
import { View, Text } from 'react-native'
import { AppModal } from '@/components/ui/AppModal'
import { Button } from '@/components/ui/Button'
import { GlassSheet } from '@/components/ui/GlassSheet'
import Animated from 'react-native-reanimated'
import { useTranslation } from 'react-i18next'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { impact, ImpactStyle, selection } from '@/lib/utils/haptics'
import { Building2, Users, X } from '@/components/ui/icons'
import { Pressable } from '@/components/ui/Pressable'
import { useSemanticColors, SPACING, RADIUS } from '@/lib/theme/tokens'
import { COLORS, FONTS } from '@/lib/brand'
import { useTheme } from '@/lib/hooks/use-theme'
import { useEntrance } from '@/lib/hooks/use-entrance'

interface CompanyDetectedSheetProps {
  visible: boolean
  companyName: string
  onCreateOrg: () => void
  onJoinOrg: () => void
  onSkip: () => void
  onClose: () => void
}

function ModalSheetButton({
  title,
  onPress,
  variant = 'primary',
  icon,
  testID,
}: {
  title: string
  onPress: () => void
  variant?: 'primary' | 'secondary'
  icon?: ReactNode
  testID: string
}) {
  return (
    <Button
      title={title}
      onPress={onPress}
      variant={variant}
      icon={icon}
      size="lg"
      testID={testID}
      style={{ width: '100%' }}
    />
  )
}

export function CompanyDetectedSheet({
  visible,
  companyName,
  onCreateOrg,
  onJoinOrg,
  onSkip,
  onClose,
}: CompanyDetectedSheetProps) {
  const enter = useEntrance()
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const insets = useSafeAreaInsets()

  return (
    <AppModal
      visible={visible}
      transparent
      animationType="slide"
      onRequestClose={onClose}
      statusBarTranslucent
    >
      <View
        style={{
          flex: 1,
          justifyContent: 'flex-end',
        }}
      >
        <Pressable
          onPress={onClose}
          accessibilityRole="button"
          accessibilityLabel={t('common.close')}
          testID="org.detected-backdrop"
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
          }}
        />
        <GlassSheet
          accessibilityLabel={t('org.detected_title')}
          style={{
            paddingBottom: Math.max(insets.bottom, SPACING.lg),
          }}
        >
          {/* Handle bar */}
          <View style={{ alignItems: 'center', paddingVertical: SPACING.sm + SPACING.xs }}>
            <View
              style={{
                width: 36,
                height: 4,
                borderRadius: RADIUS.xs,
                backgroundColor: colors.border,
              }}
            />
          </View>

          {/* Close button */}
          <View style={{ position: 'absolute', top: SPACING.sm, right: SPACING.md }}>
            <Pressable
              onPress={onClose}
              accessibilityRole="button"
              accessibilityLabel={t('common.close')}
              testID="org.detected-close"
              style={{
                width: 36,
                height: 36,
                borderRadius: 18,
                backgroundColor: colors.surfaceSubtle,
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <X size={18} color={colors.textSecondary} strokeWidth={2} />
            </Pressable>
          </View>

          <View style={{ paddingHorizontal: SPACING.lg }}>
            {/* Icon + Title */}
            <Animated.View
              collapsable={false}
              entering={enter(1)}
              style={{ alignItems: 'center', marginTop: SPACING.sm }}
            >
              <View
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: RADIUS.lg,
                  backgroundColor: `${COLORS.purple.DEFAULT}12`,
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: SPACING.md,
                }}
              >
                <Building2 size={32} color={COLORS.purple.DEFAULT} strokeWidth={1.5} />
              </View>

              <Text
                style={{
                  fontSize: 20,
                  fontFamily: FONTS.bodyBold,
                  color: colors.textPrimary,
                  textAlign: 'center',
                }}
              >
                {t('org.detected_title')}
              </Text>
            </Animated.View>

            {/* Detected company name */}
            <Animated.View
              collapsable={false}
              entering={enter(2)}
              style={{
                marginTop: SPACING.md,
                padding: SPACING.md,
                borderRadius: RADIUS.md,
                backgroundColor: isDark ? colors.surfaceSubtle : `${COLORS.purple.DEFAULT}0A`,
                borderWidth: 1,
                borderColor: isDark ? colors.border : `${COLORS.purple.DEFAULT}14`,
              }}
            >
              <Text
                style={{
                  fontSize: 16,
                  fontFamily: FONTS.bodyBold,
                  color: colors.textBrand,
                  textAlign: 'center',
                }}
                numberOfLines={2}
              >
                {companyName}
              </Text>
            </Animated.View>

            {/* Description */}
            <Animated.View collapsable={false} entering={enter(3)}>
              <Text
                style={{
                  fontSize: 14,
                  fontFamily: FONTS.body,
                  color: colors.textSecondary,
                  textAlign: 'center',
                  marginTop: SPACING.md,
                  lineHeight: 22,
                }}
              >
                {t('org.detected_desc')}
              </Text>
            </Animated.View>

            {/* Actions */}
            <Animated.View
              collapsable={false}
              entering={enter(4)}
              style={{ marginTop: SPACING.lg, gap: SPACING.sm }}
            >
              {/* Create Organization — primary CTA */}
              <ModalSheetButton
                title={t('org.create')}
                onPress={() => {
                  impact(ImpactStyle.Light)
                  onCreateOrg()
                }}
                variant="primary"
                icon={<Building2 size={20} color={COLORS.white} strokeWidth={1.8} />}
                testID="org.create-button"
              />

              {/* Join Existing — secondary */}
              <ModalSheetButton
                title={t('org.join')}
                onPress={() => {
                  void selection()
                  onJoinOrg()
                }}
                variant="secondary"
                icon={<Users size={20} color={COLORS.coral.DEFAULT} strokeWidth={1.8} />}
                testID="org.join-button"
              />

              {/* Skip — text link */}
              <Pressable
                onPress={() => {
                  void selection()
                  onSkip()
                }}
                accessibilityRole="button"
                accessibilityLabel={t('org.skip')}
                testID="org.skip-button"
                style={{
                  alignItems: 'center',
                  paddingVertical: SPACING.md,
                }}
              >
                <Text
                  style={{
                    fontSize: 14,
                    fontFamily: FONTS.body,
                    color: colors.textSecondary,
                  }}
                >
                  {t('org.skip')}
                </Text>
              </Pressable>

              {/* Hint */}
              <Animated.View entering={enter(8)}>
                <Text
                  style={{
                    fontSize: 12,
                    fontFamily: FONTS.body,
                    color: colors.textMuted,
                    textAlign: 'center',
                    marginBottom: SPACING.xs,
                  }}
                >
                  {t('org.skip_hint')}
                </Text>
              </Animated.View>
            </Animated.View>
          </View>
        </GlassSheet>
      </View>
    </AppModal>
  )
}
